import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const TARGET_ORIGIN = '*';
const IFRAME_ID = 'feedback-quickchat-widget';

export default function FullPathURLBroadcaster() {
  const location = useLocation();
  const lastSentRef = useRef<string | null>(null);
  const moRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const fullPathUrl = window.location.href;
    if (lastSentRef.current === fullPathUrl) return; // don't send the same URL several times

    // Single Page Applications need to send postMessage on every App link change in the following format:
    // { "type": "LOCATION_CHANGE", "fullPathUrl": << CURRENT FULL URL >>}
    const message = {
      type: 'LOCATION_CHANGE',
      fullPathUrl,
    };

    const trySend = () => {
      const el = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
      if (!el || !el.contentWindow) return false;
      window.setTimeout(() => {
        el.contentWindow.postMessage(message, TARGET_ORIGIN);
      }, 3000); // let the widget initialize its message listener
      lastSentRef.current = fullPathUrl;
      return true;
    };

    // try sending message to widget immediately
    if (trySend()) return;

    // the widget iframe element exists but isn't loaded yet, wait for its 'load'
    const el = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
    let loadedListener: (() => void) | null = null;
    if (el) {
      loadedListener = () => {
        trySend();
      };
      el.addEventListener('load', loadedListener, { once: true });
    }

    // the widget iframe doesn't exist yet, observe DOM until it appears
    if (!el && !moRef.current) {
      moRef.current = new MutationObserver(() => {
        const found = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
        if (found && found.contentWindow) {
          if (trySend()) {
            moRef.current?.disconnect();
            moRef.current = null;
          }
        } else if (found && !found.contentWindow) {
          // attach load once it’s there
          loadedListener = () => {
            trySend();
            moRef.current?.disconnect();
            moRef.current = null;
          };
          found.addEventListener('load', loadedListener, { once: true });
        }
      });
      moRef.current.observe(document.body, { childList: true, subtree: true });
    }

    // stop trying after e.g. 10s to prevent leaks
    if (!timeoutRef.current) {
      timeoutRef.current = window.setTimeout(() => {
        moRef.current?.disconnect();
        moRef.current = null;
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = null;
      }, 10_000);
    }

    return () => {
      if (loadedListener && el) el.removeEventListener('load', loadedListener);
    };
  }, [location.pathname, location.search, location.hash]);

  return null;
}
